<script lang="ts">
	import {
		DoubleDeviceButton,
		DeviceButton,
		Text,
		DoubleDeviceButtonV2,
		DeviceButtonV2,
		type DeviceButtonV2PropsTypes,
		type DoubleDeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { mathNormalize } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//services
	import { format } from 'svelte-i18n';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { VCInterfaceStatus } from 'src/types/VirtualComponents';

	export let device: Device, statuses: VCInterfaceStatus[];
	export let useV2Components = false;
	export let devBtnsV2Props: {
		deviceButtonV2?: Partial<DeviceButtonV2PropsTypes>;
		doubleDeviceButtonV2?: Partial<DoubleDeviceButtonV2PropsTypes>;
	} = {
		deviceButtonV2: { size: 's' },
		doubleDeviceButtonV2: { size: 's' },
	};

	let waitForTargetTempResponse = false;
	let sendTimeout: NodeJS.Timeout;
	let targetLock = false;
	let targetValue: number = null;

	const onChange = async (e: Event, stepValue: number) => {
		targetLock = true;
		e.preventDefault();
		e.stopPropagation();
		if (target && targetValue == null) {
			targetValue = Math.floor(target.event);
		}
		targetValue = mathNormalize(
			Number((targetValue + stepValue).toFixed(1)),
			target.min,
			target.max,
		);
		clearTimeout(sendTimeout);
		sendTimeout = setTimeout(sendChange, 1000, targetValue);
	};

	const sendChange = async (value: number) => {
		waitForTargetTempResponse = true;
		const res: SettingRequest = await gen2Request(device, 'Number.set', {
			id: target.id,
			value,
		});
		if (!res.isok) {
			targetLock = false;
			onChangeTargetValue(target?.event);
		}
		waitForTargetTempResponse = false;
	};

	const onChangeTargetValue = (value: number) => {
		if (targetValue == value) targetLock = false;
		if (value != null && !targetLock && targetValue != value) {
			targetValue = value;
		}
	};

	$: target = statuses.find(
		(i) =>
			i.role ==
			(statuses.find((i) => new RegExp('working_mode').test(i.role))?.event == 'dry'
				? 'target_humidity'
				: 'target_temperature'),
	);
	$: onChangeTargetValue(target?.event);
</script>

{#if target}
	{#if useV2Components}
		<DoubleDeviceButtonV2
			id="doubledevicebutton-c32oq67"
			aria-label={target?.name ||
				$format('label_thermostat_control', { default: 'Thermostat Control' })}
			isLoading={waitForTargetTempResponse}
			isActive
			{...devBtnsV2Props.doubleDeviceButtonV2}
		>
			<DeviceButtonV2
				aria-label={targetValue > (target?.event || 0)
					? $format('label_decrease', { default: 'Decrease' })
					: $format('label_increase', { default: 'Increase' })}
				id="devicebutton-fvrrt0j"
				slot="buttonLeft"
				icon="fas fa-minus"
				on:click={(e) => onChange(e, -(target?.step || 1))}
				{...devBtnsV2Props.deviceButtonV2}
			/>
			<svelte:fragment slot="content">
				<Text styleType="body-extra-small" class="text-bold mb-xxs width-full" noWrap>
					{targetValue}
				</Text>
				<Text styleType="tiny">{`${target.unit || ''}`}</Text>
			</svelte:fragment>
			<DeviceButtonV2
				aria-label={targetValue < (target?.event || 0)
					? $format('label_increase', { default: 'Increase' })
					: $format('label_decrease', { default: 'Decrease' })}
				id="devicebutton-ouafq4q"
				slot="buttonRight"
				icon="fas fa-plus"
				variant="danger"
				on:click={(e) => onChange(e, target?.step || 1)}
				{...devBtnsV2Props.deviceButtonV2}
			/>
		</DoubleDeviceButtonV2>
	{:else}
		<DoubleDeviceButton
			id="doubledevicebutton-c32oq67"
			aria-label={target?.name ||
				$format('label_thermostat_control', { default: 'Thermostat Control' })}
			isLoading={waitForTargetTempResponse}
			isActive
		>
			<DeviceButton
				aria-label={targetValue > (target?.event || 0)
					? $format('label_decrease', { default: 'Decrease' })
					: $format('label_increase', { default: 'Increase' })}
				id="devicebutton-fvrrt0j"
				slot="buttonLeft"
				icon="fas fa-minus"
				on:click={(e) => onChange(e, -(target?.step || 1))}
			/>
			<svelte:fragment slot="content">
				<Text styleType="controls-large" class="text-bold">
					{targetValue}
				</Text>
				<Text styleType="controls-small">{`${target.unit || ''}`}</Text>
			</svelte:fragment>
			<DeviceButton
				aria-label={targetValue < (target?.event || 0)
					? $format('label_increase', { default: 'Increase' })
					: $format('label_decrease', { default: 'Decrease' })}
				id="devicebutton-ouafq4q"
				slot="buttonRight"
				icon="fas fa-plus"
				variant="danger"
				on:click={(e) => onChange(e, target?.step || 1)}
			/>
		</DoubleDeviceButton>
	{/if}
{/if}
